function tslCustomCartIdentity(item) {
  const properties = Object.fromEntries(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  return JSON.stringify([String(item.variant_id), properties, item.selling_plan_allocation || null]);
}

function tslCustomCartFingerprint(cart) {
  return JSON.stringify([cart.currency, cart.items.map((item) => [tslCustomCartIdentity(item), item.quantity, item.original_price, item.final_price]), cart.discount_codes || []]);
}

function tslCustomCartOffer(cart, tiers) {
  const lines = cart.items.filter((item) => item.handle === 'custom-terpene-blend');
  if (cart.currency !== 'USD' || !lines.length || lines.some((item) => item.selling_plan_allocation || !Number.isInteger(item.quantity) || item.quantity < 1)) return null;
  const quantity = lines.reduce((total, item) => total + item.quantity, 0);
  const percentageFor = (n) => tiers.reduce((rate, tier) => n >= tier.quantity ? tier.percentage : rate, 0);
  const percentage = percentageFor(quantity * 2);
  const original = lines.reduce((total, item) => total + item.original_line_price, 0);
  const current = lines.reduce((total, item) => total + item.final_line_price, 0);
  const currentPercentage = original > 0 ? Math.round((1 - current / original) * 100) : 0;
  if (!Number.isSafeInteger(original) || !Number.isSafeInteger(current) || !percentage || percentage <= currentPercentage || quantity >= tiers.at(-1).quantity || cart.cart_level_discount_applications?.length || (cart.discount_codes || []).some((code) => code.applicable)) return null;
  const total = lines.reduce((sum, item) => sum + Math.round(item.original_price * (100 - percentage) / 100) * item.quantity * 2, 0);
  return { lines, quantity, targetQuantity: quantity * 2, percentage, currentPercentage, anotherPercentage: percentageFor(quantity + 1), original: original * 2, total, extra: total - current };
}

class GuidedCustomCart extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.root = this.dataset.root || '/';
    this.strings = JSON.parse(this.querySelector('[data-gc-strings]').textContent);
    this.tiers = JSON.parse(this.querySelector('[data-gc-tiers]').textContent);
    this.busy = false;
    this.el('double').addEventListener('click', () => this.double());
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => { if (!this.busy) this.load(); });
    }
    this.load();
  }

  disconnectedCallback() { this.unsubscribe?.(); }
  el(key) { return this.querySelector(`[data-gc-${key}]`); }
  text(key, params = {}) { return Object.entries(params).reduce((value, [name, content]) => value.replaceAll(`{${name}}`, String(content)), this.strings[key] || key); }
  money(cents) { return new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: 'USD' }).format(cents / 100); }

  async json(path, body) {
    let response;
    try { response = await fetch(this.root + path, { method: body ? 'POST' : 'GET', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined, cache: 'no-store', signal: AbortSignal.timeout(15000) }); }
    catch { throw new Error(this.text('request_error')); }
    const data = await response.json().catch(() => null);
    if (!response.ok || !data) throw new Error(data?.description || this.text('request_error'));
    return data;
  }

  async load() {
    const version = this.version = (this.version || 0) + 1;
    try {
      const cart = await this.json('cart.js');
      if (!this.isConnected || version !== this.version || this.busy) return;
      this.cart = cart;
      this.offer = tslCustomCartOffer(cart, this.tiers);
      let receipt;
      try {
        receipt = JSON.parse(sessionStorage.getItem('tsl-guided-cart-receipt'));
        sessionStorage.removeItem('tsl-guided-cart-receipt');
      } catch {}
      const confirmed = receipt?.fingerprint === tslCustomCartFingerprint(cart);
      this.el('status').textContent = confirmed ? receipt.message : '';
      document.body.toggleAttribute('data-tsl-custom-cart', Boolean(this.offer || confirmed));
      this.hidden = !this.offer && !confirmed;
      this.el('offer').hidden = !this.offer;
      if (confirmed) {
        this.el('status').focus({ preventScroll: true });
        this.scrollIntoView({ block: 'center' });
      }
      if (!this.offer) return;
      const offer = this.offer;
      this.el('offer').hidden = false;
      this.el('earned').textContent = offer.currentPercentage ? this.text('cart_saving', { quantity: offer.quantity, percentage: offer.currentPercentage }) : this.text(offer.quantity === 1 ? 'cart_count_one' : 'cart_count', { quantity: offer.quantity });
      this.el('heading').textContent = this.text('cart_title', { percentage: offer.percentage });
      this.el('detail').textContent = this.text('cart_matching', { quantity: offer.quantity, total: offer.targetQuantity });
      this.el('price').textContent = this.money(offer.total);
      this.el('original').textContent = this.money(offer.original);
      this.el('total-label').textContent = this.text('cart_projected', { quantity: offer.targetQuantity });
      this.el('extra').textContent = this.text('cart_extra', { amount: this.money(offer.extra) });
      this.el('double').textContent = this.text('cart_double', { percentage: offer.percentage });
      this.el('another').textContent = this.text('cart_another', { percentage: offer.anotherPercentage });
    } catch {
      this.hidden = true;
      document.body.removeAttribute('data-tsl-custom-cart');
    }
  }

  async double() {
    if (this.busy || !this.offer) return;
    const expected = tslCustomCartFingerprint(this.cart);
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.el('double').disabled = true;
    this.el('another').hidden = true;
    this.el('status').textContent = this.text('cart_updating');
    let changed = false;
    let before;
    try {
      before = await this.json('cart.js');
      if (tslCustomCartFingerprint(before) !== expected) throw new Error(this.text('cart_changed'));
      const offer = tslCustomCartOffer(before, this.tiers);
      if (!offer) throw new Error(this.text('cart_changed'));
      const product = await this.json('products/custom-terpene-blend.js');
      if (offer.lines.some((item) => !product.variants.some((variant) => variant.id === item.variant_id && variant.available && variant.price === item.original_price))) throw new Error(this.text('unavailable'));
      if (tslCustomCartFingerprint(await this.json('cart.js')) !== expected) throw new Error(this.text('cart_changed'));
      const updates = Object.fromEntries(offer.lines.map((item) => [item.key, item.quantity * 2]));
      const after = await this.json('cart/update.js', { updates });
      changed = true;
      const quantitiesMatch = after.items.length === before.items.length && before.items.every((item) => after.items.some((updated) => tslCustomCartIdentity(item) === tslCustomCartIdentity(updated) && updated.quantity === item.quantity * (item.handle === 'custom-terpene-blend' ? 2 : 1)));
      const actual = after.items.filter((item) => item.handle === 'custom-terpene-blend').reduce((total, item) => total + item.final_line_price, 0);
      if (!quantitiesMatch || actual > offer.total) {
        const rollback = Object.fromEntries(after.items.filter((item) => item.handle === 'custom-terpene-blend').map((item) => [item.key, before.items.find((original) => tslCustomCartIdentity(item) === tslCustomCartIdentity(original))?.quantity ?? item.quantity]));
        await this.json('cart/update.js', { updates: rollback });
        changed = false;
        throw new Error(this.text('cart_price_error'));
      }
      try { sessionStorage.setItem('tsl-guided-cart-receipt', JSON.stringify({ fingerprint: tslCustomCartFingerprint(after), message: this.text('cart_added', { quantity: offer.targetQuantity, percentage: offer.percentage }) })); } catch {}
      window.location.reload();
    } catch (error) {
      this.el('status').textContent = this.text('cart_error', { error: error.message });
      if (changed) this.el('status').textContent = this.text('cart_changed');
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.el('double').disabled = false;
      this.el('another').hidden = false;
    }
  }
}

if (!customElements.get('tsl-guided-cart')) customElements.define('tsl-guided-cart', GuidedCustomCart);
